import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { visaApi, VISA_STATUSES, VISA_STATUS_BY_ID, VISA_PHASES } from "@/lib/visa";
import { countryFlag, relativeTime } from "@/lib/format";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const roleBase = (role) => ({ admin: "/admin", coordinator: "/coordinator", partner: "/partner", patient: "/portal" }[role] || "/admin");

export default function VisaStatusTracker() {
  const { user } = useAuth();
  const base = roleBase(user?.role);
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState("all");

  const load = async () => {
    try { setLoading(true); setItems(await visaApi.listRequests()); }
    catch (e) { toast.error(getApiErrorMessage(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const visibleStatuses = phase === "all" ? VISA_STATUSES : VISA_STATUSES.filter((s) => s.phase === phase);

  return (
    <div data-testid="visa-tracker-page">
      <PageHeader eyebrow="International Services" title="Visa Status Tracker"
        subtitle="Kanban view of every visa invitation request across the 13-step pipeline." />

      <div className="flex flex-wrap gap-2 mb-5">
        {["all", ...VISA_PHASES].map((p) => (
          <button key={p} onClick={() => setPhase(p)}
            data-testid={`phase-filter-${p}`}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold border ${phase === p ? "bg-[#2A4D3E] text-white border-[#2A4D3E]" : "bg-white text-[#5C6660] border-[#E8E6E1] hover:bg-[#FAFAF7]"}`}>
            {p === "all" ? "All phases" : p}
          </button>
        ))}
      </div>

      {loading ? <div className="text-sm text-[#5C6660]">Loading…</div> : (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-3" style={{ minWidth: `${visibleStatuses.length * 300}px` }}>
            {visibleStatuses.map((s) => {
              const col = items.filter((r) => r.status === s.id);
              return (
                <div key={s.id} className="flex-shrink-0 w-72" data-testid={`kanban-col-${s.id}`}>
                  <div className="rounded-t-xl px-3 py-2 flex items-center justify-between" style={{ background: s.bg }}>
                    <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: s.color }}>{s.label}</div>
                    <span className="bg-white text-[11px] font-semibold rounded-full px-2 py-0.5" style={{ color: s.color }}>{col.length}</span>
                  </div>
                  <div className="bg-[#FAFAF7] border-x border-b border-[#E8E6E1] rounded-b-xl p-2 space-y-2 min-h-[200px]">
                    {col.length === 0 ? (
                      <div className="text-[11px] text-[#8E9992] text-center py-8">Empty</div>
                    ) : col.map((r) => (
                      <button key={r.id} onClick={() => nav(`${base}/visa/${r.id}`)}
                        className="w-full text-left bg-white border border-[#E8E6E1] rounded-lg p-3 hover:border-[#2A4D3E]" data-testid={`kanban-card-${r.id}`}>
                        <div className="text-sm font-medium text-[#2A332C] truncate">{r.patient_info?.full_name || "—"}</div>
                        <div className="text-[11px] text-[#8E9992] mt-0.5">{countryFlag(r.patient_info?.nationality)} {r.patient_info?.nationality} · {r.reference_number || "no ref"}</div>
                        <div className="text-[11px] text-[#8E9992] mt-1">{relativeTime(r.updated_at || r.created_at)}</div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
